import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Upload } from "antd";
import { isNil, size } from "lodash";
import { aiccLessonInformationApi } from "services/aiccLearningAssets";
import { AiccLessonInformation, AiccLessonInformationPayload } from "types/model/aiccLearningAssets";
import AgenticAssetLink from "../AgenticAssetLink";
import LearningAssetPage from "../LearningAssetPage";
import LessonEntryFilters from "../LessonEntryFilters";
import { useLearningAssetVersions } from "../hooks/useLearningAssetVersions";
import { useLessonEntryFilters } from "../hooks/useLessonEntryFilters";
import { useLessonEntryPagination } from "../hooks/useLessonEntryPagination";
import { useAssetImport } from "../hooks/useAssetImport";
import UploadPreviewModal from "../UploadPreviewModal";
import getColumns, { previewColumns } from "./columnConfig";
import LessonInformationModal from "./LessonInformationModal";

const LESSON_INFORMATION_TITLE = "Lesson Information";

const LESSON_INFORMATION_INTRO =
  "A summary of vocabulary and phrases covered in your Pimsleur lessons. It gives the AI context about what " +
  "you've already learned so conversations stay relevant to your level.";

const FILE_ACCEPT = ".zip";

const EMPTY_LESSONS: AiccLessonInformation[] = [];

function LessonInformationManagement() {
  const state = useLearningAssetVersions(aiccLessonInformationApi.fetchVersions);
  const {
    languageId,
    items,
    filters: lessonFilters,
    groupStatus,
    isEditable,
    isDraft,
    canManage,
    isLoading,
    isSaving,
    runWrite,
  } = state;

  const lessons = isDraft ? EMPTY_LESSONS : items;

  const {
    entries,
    levelOptions,
    lessonOptions,
    selectedLevels,
    selectedLessons,
    setSelectedLevels,
    setSelectedLessons,
  } = useLessonEntryFilters(lessons, lessonFilters);

  const pagination = useLessonEntryPagination(entries, groupStatus);

  const { uploadFile, preview, isValidating, closeUpload, selectFile, confirmUpload } = useAssetImport({
    api: aiccLessonInformationApi,
    languageId,
    isDraft,
    runWrite,
  });

  const isUploading = isValidating || !isNil(preview);
  const isActionDisabled = isNil(languageId) || isUploading;
  const previewLessons = preview?.rows ?? EMPTY_LESSONS;

  const [editingLesson, setEditingLesson] = useState<AiccLessonInformation>();

  const closeModal = useCallback(() => setEditingLesson(undefined), []);

  useEffect(() => {
    closeModal();
  }, [closeModal, groupStatus, languageId]);

  const handleSubmit = useCallback(
    (payload: AiccLessonInformationPayload) => {
      if (isNil(editingLesson)) {
        return;
      }
      const { level, lesson } = editingLesson;
      runWrite(
        (id) => aiccLessonInformationApi.update(id, editingLesson, payload),
        `Updated Level ${level} Lesson ${lesson} successfully.`,
        closeModal
      );
    },
    [closeModal, editingLesson, runWrite]
  );

  const columns = useMemo(() => getColumns({ canManage, isActionDisabled: isUploading, onEdit: setEditingLesson }), [
    canManage,
    isUploading,
  ]);

  return (
    <LearningAssetPage<AiccLessonInformation>
      title={LESSON_INFORMATION_TITLE}
      fieldGuideIntro={LESSON_INFORMATION_INTRO}
      headerExtra={<AgenticAssetLink />}
      columns={columns}
      state={{ ...state, items: entries }}
      pagination={pagination}
      filters={
        <LessonEntryFilters
          levelOptions={levelOptions}
          lessonOptions={lessonOptions}
          selectedLevels={selectedLevels}
          selectedLessons={selectedLessons}
          setSelectedLevels={setSelectedLevels}
          setSelectedLessons={setSelectedLessons}
          disabled={isLoading}
        />
      }
      isLocked={isUploading}
      toolbarActions={
        isEditable && (
          <Upload accept={FILE_ACCEPT} showUploadList={false} beforeUpload={selectFile} disabled={isActionDisabled}>
            <Button type="primary" shape="round" loading={isValidating} disabled={isActionDisabled}>
              Upload
            </Button>
          </Upload>
        )
      }
    >
      <LessonInformationModal
        visible={!isNil(editingLesson)}
        lesson={editingLesson}
        isSaving={isSaving}
        onCancel={closeModal}
        onSubmit={handleSubmit}
      />
      <UploadPreviewModal<AiccLessonInformation>
        visible={!isNil(preview)}
        fileName={uploadFile?.name}
        preview={preview}
        rows={previewLessons}
        columns={previewColumns}
        rowKey="id"
        summary={`${size(
          previewLessons
        )} lessons are ready to upload. Uploading replaces all current content for this language.`}
        isSaving={isSaving}
        onCancel={closeUpload}
        onConfirm={confirmUpload}
      />
    </LearningAssetPage>
  );
}

export default LessonInformationManagement;
